import type { FogShape } from "../../shared/fog.js";
import type { HazardZoneDTO, SnapshotDTO, TankDTO } from "../../shared/protocol/index.js";

// Render this far in the past so we always have two snapshots to interpolate
// between. Must exceed the network send interval (≈66ms at 15 Hz) with margin.
export const INTERP_DELAY = 140;

// Rapid fire's shots come fast — play its "pew" a bit faster/shorter so a
// burst doesn't sound mushy at the configured fire rate.
export const RAPIDFIRE_PEW_RATE = 1.4;

// Bullets are rendered in the owning tank's color so shots are identifiable by
// shooter. Size varies a little by kind. Tracking rounds render as a triangle
// (drawn separately), the rest as filled circles.
const BULLET_COLOR = "#11100e";
/** A bullet takes the owning tank's color, falling back to the dark base. */
export function bulletColor(tankColor: string | undefined): string {
  return tankColor ?? BULLET_COLOR;
}
/** Radius adjustment for a bullet, composed from its effect flags (explosive
 *  reads biggest; homing slightly larger; plain/sniper unchanged). */
export function bulletDr(b: { homing: boolean; explosive: boolean }): number {
  if (b.explosive) return 2;
  if (b.homing) return 1;
  return 0;
}

// Hazard zone glyphs — mirror the emblem and color of each hazard's toggle in
// the lobby config so the map terrain matches what the player enabled.
export const HAZARD_ICON: Record<HazardZoneDTO["type"], { glyph: string; color: string }> = {
  lava: { glyph: "♨", color: "#c2452f" },
  mud: { glyph: "≋", color: "#8a6b3f" },
  ice: { glyph: "❄", color: "#2fb8d6" },
  heal: { glyph: "✚", color: "#4fd6a0" },
};

export interface Buffered {
  snap: SnapshotDTO;
  recvAt: number;
}

export interface Explosion {
  x: number;
  y: number;
  color: string;
  start: number; // ms
}

export const EXPLOSION_MS = 600;
export const BEAM_MS = 170;

// Game-feel effect timings (ms).
export const MUZZLE_MS = 90;
export const RECOIL_MS = 130;
export const HIT_FLASH_MS = 220;
export const SPAWN_ANIM_MS = 380;

// Honor the OS-level reduced-motion preference: no screen shake, trails or
// ambient particles. Core transients (explosion flash/ring) still draw so
// gameplay stays readable.
export const REDUCED_MOTION =
  typeof window !== "undefined" &&
  window.matchMedia?.("(prefers-reduced-motion: reduce)").matches === true;

// Hard cap on live particles so effect-heavy fights can't melt low-end phones.
export const PARTICLE_CAP = 320;

export interface Beam {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  start: number;
}

export interface Particle {
  x: number;
  y: number;
  vx: number; // px/s
  vy: number; // px/s
  size: number; // px radius (or square half-extent)
  color: string;
  alpha: number; // starting opacity; fades to 0 over life
  born: number; // ms
  life: number; // ms
  drag: number; // 1/s velocity damping
  grow: number; // size multiplier at end of life (smoke grows, sparks shrink)
  square: boolean; // debris chunks render as squares, everything else circles
}

/** A one-shot expanding/contracting ring (spawn-in, power-up pickup). */
export interface Ring {
  x: number;
  y: number;
  color: string;
  born: number; // ms
  life: number; // ms
  r0: number; // start radius
  r1: number; // end radius
  width: number;
}

/** Per-tank transient visual state (muzzle flash / recoil / hit / spawn-in). */
export interface TankFx {
  muzzleUntil: number;
  recoilAt: number;
  hitAt: number;
  spawnAt: number;
}

export interface FogSource {
  x: number;
  y: number;
  haloRadius: number;
  shape: FogShape;
}

export interface FogView {
  local: TankDTO;
  sources: FogSource[];
}
